<template>
  <AnonLayout>
    <AnonFormWrapper title="Login">
      <form @submit.prevent="handleSubmit">
        <div class="mb-4">
          <label class="mb-2 inline-block" for="email">Email</label>
          <VInput id="email" type="email" v-model="email" autocomplete="username webauthn" autofocus />
        </div>
        <div class="mb-4">
          <label class="mb-2 inline-block" for="password">Password</label>
          <VInput id="password" type="password" v-model="password" autocomplete="current-password" />
          <span class="mt-1 block text-right">
            <router-link
              to="/forgot"
              class="text-[13px] text-red-400 hover:text-red-700 hover:underline"
            >
              Forgot your password?
            </router-link>
          </span>
        </div>
        <UIButtonNew
          type="submit"
          :is-loading="isSubmitting"
          is-loading-text="Logging in..."
          class="mb-2 w-full"
        >
          Login
        </UIButtonNew>
      </form>
      <div class="relative mt-6">
        <div class="absolute inset-0 flex items-center">
          <div class="w-full border-t border-gray-300"></div>
        </div>
        <div class="relative flex justify-center text-sm">
          <span class="bg-white px-2 text-gray-500">Or</span>
        </div>
      </div>
      <div class="mt-6 flex justify-center">
        <div ref="googleButtonRef"></div>
      </div>
      <p v-if="googleError" class="mt-2 text-center text-sm text-red-600">
        {{ googleError }}
      </p>
    </AnonFormWrapper>
  </AnonLayout>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import { useToast } from 'vue-toastification';
import NProgress from 'nprogress';
import AnonLayout from '@/layouts/AnonLayout.vue';
import AnonFormWrapper from '@/components/_common/AnonFormWrapper.vue';
import VInput from '@/components/ui/VInput.vue';
import UIButtonNew from '@/components/ui/UIButtonNew/UIButtonNew.vue';
import { useGoogleSignIn } from '@/composables/useGoogleSignIn';
import {
  isConditionalMediationAvailable,
  startConditionalAuthentication,
} from '@/composables/useWebAuthn';
import { VITE_APP_GOOGLE_CLIENT_ID } from '@/config/environment';

const store = useStore();
const router = useRouter();
const route = useRoute();
const toast = useToast();

const email = ref('');
const password = ref('');
const isSubmitting = ref(false);

const isLoggedIn = computed(() => store.getters['auth/check']);

const googleButtonRef = ref(null);
const { error: googleError, init: initGoogleSignIn } = useGoogleSignIn({
  clientId: VITE_APP_GOOGLE_CLIENT_ID,
  buttonRef: googleButtonRef,
  onError: (message) => toast.error(message),
});

let conditionalAbort = null;

async function startConditionalPasskeyLogin() {
  try {
    const available = await isConditionalMediationAvailable();
    if (!available) return;
    const options = await store.dispatch('auth/fetchPasskeyAuthOptions');
    const credential = await startConditionalAuthentication(options, conditionalAbort?.signal);
    if (!credential) return;
    await store.dispatch('auth/passkeyLogin', {
      credential,
      next: route.query.next,
    });
  } catch (err) {
    if (err?.name !== 'AbortError' && err?.name !== 'NotAllowedError') {
      console.debug('[Passkey] Conditional login failed:', err);
    }
  }
}

onUnmounted(() => {
  conditionalAbort?.abort();
});

function handleSubmit() {
  conditionalAbort?.abort();
  NProgress.start();
  isSubmitting.value = true;

  store
    .dispatch('auth/login', {
      credentials: {
        email: email.value,
        password: password.value,
      },
      next: route.query.next,
    })
    .catch((e) => {
      toast.error(e.response?.data?.message || 'An error occurred.');
      NProgress.done();
      isSubmitting.value = false;
    });
}

watch(isLoggedIn, (val) => {
  if (val) {
    router.push({ name: 'DeviceListView' });
  }
});

onMounted(() => {
  if (isLoggedIn.value) {
    router.push({ name: 'DeviceListView' });
    return;
  }
  initGoogleSignIn((idToken) =>
    store.dispatch('auth/googleLogin', {
      id_token: idToken,
      next: route.query.next,
    }),
  );
  conditionalAbort = new AbortController();
  startConditionalPasskeyLogin();
});
</script>
